'use client'

import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import * as React from 'react'
import { cn } from '#app/shared/lib/misc.js'
import { Icon } from '../icons/Icon.tsx'

const AccordionRoot = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>((props, ref) => <AccordionPrimitive.Item ref={ref} {...props} />)
AccordionItem.displayName = 'AccordionItem'

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        `
          flex flex-1 items-center justify-between py-4 text-sm
          text-subtitle1-archivo uppercase transition-all

          [&[data-state=open]>div>svg]:rotate-180

          hover:underline
        `,
        className,
      )}
      {...props}
    >
      {children}
      <div
        className={`
          flex h-5 w-5 items-center justify-center border border-[#D9D9DE]
        `}
      >
        <ChevronDownIcon
          className={`
            text-muted-foreground h-3 w-3 shrink-0 transition-transform
            duration-200
          `}
        />
      </div>
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={`
      overflow-hidden text-sm

      data-[state=closed]:animate-accordion-up

      data-[state=open]:animate-accordion-down
    `}
    {...props}
  >
    <div className={cn(className)}>{children}</div>
  </AccordionPrimitive.Content>
))
AccordionContent.displayName = AccordionPrimitive.Content.displayName

const AccordionTriggerFaq = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        `
          group z-[1] flex flex-1 items-center justify-between gap-4 border-b
          border-neutral-75 bg-white p-6 py-[23px] text-start text-h5-bold
          text-neutral-900 transition-all

          data-[state=open]:rounded-b-none data-[state=open]:border-b-0
        `,
        className,
      )}
      {...props}
    >
      {children}
      <Icon
        name="basic/plus"
        size="24px"
        className={`
          self-start text-neutral-900

          group-data-[state=open]:hidden
        `}
        strokeWidth={4}
      />
      <Icon
        name="basic/minus"
        size="24px"
        className={`
          hidden self-start text-neutral-900

          group-data-[state=open]:block
        `}
      />
      {/* <ChevronDownIcon className="text-muted-foreground h-4 w-4 shrink-0 transition-transform duration-200" /> */}
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))

const AccordionContentFaq = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={`
      border-neutral-75 pb-6

      data-[state=open]:border-b
    `}
    {...props}
  >
    <div className={cn(className)}>{children}</div>
  </AccordionPrimitive.Content>
))

const AccordionTriggerBordered = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        `
          flex h-[44px] flex-1 items-center justify-between rounded-[4px]
          border-2 border-gray-200 px-4 font-archivo text-sm font-bold

          [&[data-state=open]>svg]:rotate-180

          data-[state=open]:rounded-b-none data-[state=open]:border-b-0

          md:h-[64px] md:px-6 md:text-base
        `,
        className,
      )}
      {...props}
    >
      {children}
      <Icon
        name="basic/plus"
        className={`
          text-muted-foreground h-5 min-h-5 w-5 min-w-5 shrink-0
          transition-transform duration-200

          md:h-7 md:min-h-7 md:w-7 md:min-w-7
        `}
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTriggerBordered.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContentBordered = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={`
      overflow-hidden rounded-b-[4px] border-2 border-t-0 border-gray-200 pl-7
      pr-6

      data-[state=closed]:animate-accordion-up

      data-[state=open]:animate-accordion-down
    `}
    {...props}
  >
    <div className={cn(className, 'pb-6')}>{children}</div>
  </AccordionPrimitive.Content>
))
AccordionContentBordered.displayName = AccordionPrimitive.Content.displayName

type AccordionItems = {
  trigger: React.ReactNode
  content: React.ReactNode
}
type AccordionProps = {
  type?: 'single' | 'multiple'
  collapsible?: boolean
  accordionItems: AccordionItems[]
  className?: string
  variant?: 'transparent' | 'faq' | 'bordered' | 'bordered-faq'
  defaultValue?: string[] | string
  itemClassName?: (item: AccordionItems, key: number) => string
}

const Accordion = ({
  type = 'single',
  collapsible = true,
  variant = 'transparent',
  className,
  accordionItems,
  defaultValue,
  itemClassName,
}: AccordionProps) => {
  return (
    <AccordionRoot
      type={type}
      collapsible={collapsible}
      className={cn('w-full bg-white', className)}
      defaultValue={defaultValue as any}
    >
      {accordionItems.map((item, key) => (
        <AccordionItem
          className={cn(itemClassName?.(item, key), 'bg-white')}
          key={`item-${key}`}
          value={`item-${key}`}
        >
          {variant === 'faq' ? (
            <>
              <AccordionTriggerFaq>{item.trigger}</AccordionTriggerFaq>
              <AccordionContentFaq>{item.content}</AccordionContentFaq>
            </>
          ) : variant === 'transparent' ? (
            <>
              <AccordionTrigger>{item.trigger}</AccordionTrigger>
              <AccordionContent>{item.content}</AccordionContent>
            </>
          ) : variant === 'bordered' || variant === 'bordered-faq' ? (
            <>
              <AccordionTriggerBordered
                className={
                  variant === 'bordered-faq'
                    ? 'h-auto gap-x-4 p-4 text-left'
                    : ''
                }
              >
                {item.trigger}
              </AccordionTriggerBordered>
              <AccordionContentBordered>
                {item.content}
              </AccordionContentBordered>
            </>
          ) : null}
        </AccordionItem>
      ))}
    </AccordionRoot>
  )
}

export {
  Accordion,
  AccordionRoot,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
}
